'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { pillBtnBase } from './pill-button'

// Bottom-center toast with optional Undo — ui.jsx:171-194. The prototype uses
// stone-900 (#1C1917) in both themes, so the color is hardcoded here.
export interface RToastData {
  msg: string
  undo?: () => void
}

export interface RToastProps {
  toast: RToastData | null
  onClose: () => void
  offset?: number
}

export function RToast({ toast, onClose, offset = 24 }: RToastProps) {
  React.useEffect(() => {
    if (!toast) return
    const t = setTimeout(onClose, 5000)
    return () => clearTimeout(t)
  }, [toast, onClose])
  if (!toast) return null
  return (
    <div
      className={cn(
        'fixed left-1/2 z-[100] flex -translate-x-1/2 animate-toast-in items-center gap-[14px]',
        'whitespace-nowrap rounded-full bg-[#1C1917] py-[10px] pl-[18px] pr-[10px]',
        'text-[13.5px] font-medium text-white shadow-e3'
      )}
      style={{ bottom: offset }}
    >
      <span>{toast.msg}</span>
      {toast.undo ? (
        <button
          type="button"
          className={cn(pillBtnBase, 'bg-white/[0.14] px-[14px] py-[6px] text-[13px] text-[#FCA5A5]')}
          onClick={() => {
            toast.undo?.()
            onClose()
          }}
        >
          Undo
        </button>
      ) : null}
    </div>
  )
}

// Provider + hook so pages can `useRToast().show({ msg, undo })` — the
// prototype threads showToast through a store; here it's React context.
interface RToastContextValue {
  show: (toast: RToastData) => void
}

const RToastContext = React.createContext<RToastContextValue | null>(null)

export function useRToast(): RToastContextValue {
  const ctx = React.useContext(RToastContext)
  if (!ctx) throw new Error('useRToast must be used within an RToastProvider')
  return ctx
}

export function RToastProvider({ children, offset = 24 }: { children: React.ReactNode; offset?: number }) {
  const [toast, setToast] = React.useState<RToastData | null>(null)
  const show = React.useCallback((t: RToastData) => setToast({ ...t }), [])
  const close = React.useCallback(() => setToast(null), [])
  const value = React.useMemo(() => ({ show }), [show])
  return (
    <RToastContext.Provider value={value}>
      {children}
      <RToast toast={toast} onClose={close} offset={offset} />
    </RToastContext.Provider>
  )
}
